// includes/js-src/janus-engine/pl/abilities.ts — calculate_abilities() twin (calc_pl_system.php:1137–1420): the embedded
// abilities of a root (every AbilityType bucket, `ability_choice` as max-branch × 1.25), the standalone content-priced row,
// and the natural-weapon base damage + reach. A missing / private definition is a REFUSAL (the PHP `continue` on an empty
// query_full_item_arr would under-price silently — plan D7).
import type { ItemFieldRow } from '../compile/item-field-reader';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { phpJsonTruthy } from '../compile/php-json-values';
import { phpRound, phpInt, phpIntRound } from './php-round';
import { damageCostGpDetailed } from './cost';
import { plCatalogs } from './contract';
import { plCostLayerEntryUuid, abilityEntryId, abilityEntryName } from './layer-cells';
import type { Row } from './read-context';
import { isObj } from './read-context';
import { requireDefinition } from './xref';
import { calculateSingleAttackAbilityCost, durationLabel } from './ability-cost';
import { calculateAbilityEconomyAndRestrictions } from './economy';
import { type PassEnv, type PlAccumulator, lv, le, itemField, itemType, entryRestoreItemArr, entryHasItemArr, resolveEntryItemArr, bucket, sub, plOf } from './passes';

const STANDALONE_TYPES = ['reaction_ability', 'quick_ability', 'free_ability', 'passive_ability', 'opportunity_attack_ability', 'full_ability', 'attack_ability', 'ability', 'lair_ability', 'trap_ability', 'curse', 'menta', 'sanguine', 'spell'];
const CASTABLE_OWNER_TYPES = ['spell', 'curse', 'menta', 'sanguine', 'ability', 'full_ability', 'attack_ability', 'quick_ability', 'free_ability', 'reaction_ability', 'passive_ability', 'opportunity_attack_ability'];

/** The (uuid, ability_arr, name) an embedded entry resolves to — calc_pl's two branches (nested item_arr vs bare / shell ref). */
function resolveEmbedded(env: PassEnv, rawEntry: unknown, root: Row): { uuid: string; abilityArr: Row; name: string } | null {
    let entry = rawEntry;
    if (isObj(entry)) entry = entryRestoreItemArr(entry);
    if (isObj(entry) && entryHasItemArr(entry)) {
        const abilityArr = resolveEntryItemArr(env, entry, root);
        // PHP: `if (empty($ability_arr)) continue;` — an EMPTY array skips; a non-array item_arr would fatal in
        // calculate_single_attack_ability_cost(array …). A missing definition already refused inside the resolver.
        if (abilityArr === null) throw new Error('embedded entry item_arr is not an array');
        if (Object.keys(abilityArr).length === 0) return null;
        return { uuid: abilityEntryId(entry, env.ctx.entryDeps), abilityArr, name: abilityEntryName(entry, env.ctx.entryDeps) };
    }
    const uuid = isObj(entry) || Array.isArray(entry) ? (abilityEntryId(entry, env.ctx.entryDeps) || plCostLayerEntryUuid(entry)) : (typeof entry === 'string' ? entry : '');
    if (!uuid) return null;
    const abilityArr = requireDefinition(env.ctx, uuid);
    const n = itemField(env, abilityArr, 'item_name');
    return { uuid, abilityArr, name: typeof n === 'string' ? n : 'Unknown Ability' };
}

export function calculateAbilities(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    for (const _ of calculateAbilitiesSteps(env, itemArr, acc)) { /* synchronous action/display path */ }
}

/** Same ordered calculation, with a task boundary available between embedded abilities. */
export function* calculateAbilitiesSteps(env: PassEnv, itemArr: Row, acc: PlAccumulator): Generator<void, void> {
    const t = itemType(env, itemArr);
    const isWeapon = t === 'natural_weapon' || t === 'weapon';
    let weaponTierDiscount = 1.0;
    if (isWeapon) {
        if (phpJsonTruthy(lv(env, itemArr, 'requires_skill_tier_7'))) weaponTierDiscount = 0.40;
        else if (phpJsonTruthy(lv(env, itemArr, 'requires_skill_tier_3'))) weaponTierDiscount = 0.60;
        else if (phpJsonTruthy(lv(env, itemArr, 'requires_skill_tier_2'))) weaponTierDiscount = 0.75;
    }
    const weaponAttachedMultiplier = isWeapon ? 0.25 : 1.0;
    let slotPenalty = 1.0;
    if (isWeapon) {
        for (const slotCell of le(env, itemArr, 'equipment_slot')) {
            const slotId = plCostLayerEntryUuid(slotCell);
            if (slotId === '') continue;
            const slotVar = plCatalogs(env.contract).equipmentSlots.getVariableName(slotId);
            if (slotVar === 'head' || slotVar === 'neck') { slotPenalty = 0.70; break; }
        }
    }
    const ownerDurationMult = CASTABLE_OWNER_TYPES.includes(t) ? env.costs.getDurationMultiplier(itemArr as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n)) : 1.0;
    const ownerName = (): string => { const n = itemField(env, itemArr, 'item_name'); return typeof n === 'string' ? n : 'the owner'; };
    const abilityMods = bucket(acc, 'ability_modifiers');

    // EMBEDDED ABILITIES
    for (const abilityType of env.contract.abilityTypes) {
        const abilityEntries = le(env, itemArr, abilityType);
        if (abilityEntries.length === 0) continue;

        if (abilityType === 'ability_choice') {
            const branchRows: Row = {};
            const branchFinalGps: number[] = [];
            for (const entry of le(env, itemArr, 'ability_choice')) {
                yield;
                const resolved = resolveEmbedded(env, entry, itemArr);
                if (!resolved) continue;
                const { uuid, abilityArr, name } = resolved;
                const baseData = calculateSingleAttackAbilityCost(env, abilityArr, itemArr);
                let contentGp = baseData.gp;
                let tierDiscount = env.tiers.getRequirementDiscountFromArray((Object.keys(abilityArr).length ? abilityArr : itemArr) as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n));
                if (weaponTierDiscount < 1.0) tierDiscount = Math.min(tierDiscount, weaponTierDiscount);
                contentGp = phpIntRound(contentGp * tierDiscount * weaponAttachedMultiplier * slotPenalty);
                const childType = itemType(env, abilityArr) || 'ability';
                const econ = calculateAbilityEconomyAndRestrictions(env.ctx, abilityArr, childType, itemArr, env.contract);
                if (ownerDurationMult < 1.0) {
                    econ.steps.granted_for_duration = { multiplier: ownerDurationMult, note: 'usable only while ' + ownerName() + ' lasts (' + durationLabel(env, itemArr) + ')' };
                    econ.multiplier = phpRound(econ.multiplier * ownerDurationMult, 3);
                }
                const finalGp = phpIntRound(contentGp * econ.multiplier);
                const rowKey = uuid !== '' ? uuid : ('ability_choice_branch_' + Object.keys(branchRows).length);
                branchRows[rowKey] = {
                    ability_name: name, content_gp: contentGp, base_content_details: baseData.components, total_content_before_economy: contentGp,
                    tier_discount: { multiplier: phpRound(tierDiscount, 2), gp_delta: phpIntRound(contentGp * (tierDiscount - 1)) },
                    weapon_attached_discount: { multiplier: phpRound(weaponAttachedMultiplier, 2), gp_delta: phpIntRound(contentGp * (weaponAttachedMultiplier - 1)) },
                    slot_penalty: { multiplier: phpRound(slotPenalty, 2), gp_delta: phpIntRound(contentGp * (slotPenalty - 1)) },
                    economy_details: econ.steps, final_multiplier: phpRound(econ.multiplier, 3), final_gp: finalGp, pl: plOf(finalGp),
                };
                branchFinalGps.push(finalGp);
            }
            if (branchFinalGps.length === 0) continue;
            const maxBranch = Math.max(...branchFinalGps);
            const choiceMultiplier = 1.25;
            const bucketGp = phpIntRound(maxBranch * choiceMultiplier);
            sub(abilityMods, 'ability_choice')['__ability_choice__'] = {
                ability_name: 'Ability choice (only one branch; PL = max branch × 1.25)', branch_children: branchRows, max_branch_final_gp: maxBranch, ability_choice_multiplier: choiceMultiplier,
                content_gp: bucketGp, base_content_details: [], total_content_before_economy: bucketGp,
                tier_discount: { multiplier: 1.0, gp_delta: 0 }, weapon_attached_discount: { multiplier: 1.0, gp_delta: 0 }, slot_penalty: { multiplier: 1.0, gp_delta: 0 },
                economy_details: { ability_choice: { multiplier: choiceMultiplier, note: 'Branches listed for reference; PL uses the strongest branch only, then ×1.25.' } },
                final_multiplier: choiceMultiplier, final_gp: bucketGp, pl: plOf(bucketGp),
            };
            acc.totalGp += bucketGp;
            continue;
        }

        for (const entry of abilityEntries) {
            yield;
            const resolved = resolveEmbedded(env, entry, itemArr);
            if (!resolved) continue;
            const { uuid, abilityArr, name } = resolved;
            const baseData = calculateSingleAttackAbilityCost(env, abilityArr, itemArr);
            let contentGp = baseData.gp;
            let tierDiscount = env.tiers.getRequirementDiscountFromArray((Object.keys(abilityArr).length ? abilityArr : itemArr) as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n));
            if (weaponTierDiscount < 1.0) tierDiscount = Math.min(tierDiscount, weaponTierDiscount);
            contentGp = phpIntRound(contentGp * tierDiscount * weaponAttachedMultiplier * slotPenalty);
            const econ = calculateAbilityEconomyAndRestrictions(env.ctx, abilityArr, abilityType, itemArr, env.contract);
            if (ownerDurationMult < 1.0) {
                econ.steps.granted_for_duration = { multiplier: ownerDurationMult, note: 'usable only while ' + ownerName() + ' lasts (' + durationLabel(env, itemArr) + ')' };
                econ.multiplier = phpRound(econ.multiplier * ownerDurationMult, 3);
            }
            const finalGp = phpIntRound(contentGp * econ.multiplier);
            sub(abilityMods, abilityType)[uuid] = {
                ability_name: name, content_gp: contentGp, base_content_details: baseData.components, total_content_before_economy: contentGp,
                tier_discount: { multiplier: phpRound(tierDiscount, 2), gp_delta: phpIntRound(contentGp * (tierDiscount - 1)) },
                weapon_attached_discount: { multiplier: phpRound(weaponAttachedMultiplier, 2), gp_delta: phpIntRound(contentGp * (weaponAttachedMultiplier - 1)) },
                slot_penalty: { multiplier: phpRound(slotPenalty, 2), gp_delta: phpIntRound(contentGp * (slotPenalty - 1)) },
                economy_details: econ.steps, final_multiplier: phpRound(econ.multiplier, 3), final_gp: finalGp, pl: plOf(finalGp),
            };
            acc.totalGp += finalGp;
        }
    }

    // STANDALONE ABILITY (including spells)
    if (STANDALONE_TYPES.includes(t)) {
        const uuidRaw = itemField(env, itemArr, 'item_id');
        const uuid = typeof uuidRaw === 'string' ? uuidRaw : '';
        const n = itemField(env, itemArr, 'item_name');
        const abilityName = typeof n === 'string' ? n : 'Unknown Ability';
        const baseData = calculateSingleAttackAbilityCost(env, itemArr, itemArr);
        const contentGp = baseData.gp;
        let tierDiscount = env.tiers.getRequirementDiscountFromArray(itemArr as ItemFieldRow, (r, nm) => env.ctx.layerValue(r, nm));
        if (weaponTierDiscount < 1.0) tierDiscount = Math.min(tierDiscount, weaponTierDiscount);
        const totalContentGp = phpIntRound(contentGp * tierDiscount * weaponAttachedMultiplier * slotPenalty);
        const econ = calculateAbilityEconomyAndRestrictions(env.ctx, itemArr, t, itemArr, env.contract);
        const finalGp = phpIntRound(totalContentGp * econ.multiplier);
        sub(abilityMods, t)[uuid] = {
            ability_name: abilityName, content_gp: contentGp, base_content_details: baseData.components, total_content_before_economy: totalContentGp,
            tier_discount: { multiplier: phpRound(tierDiscount, 2), gp_delta: phpIntRound(totalContentGp * (tierDiscount - 1)) },
            weapon_attached_discount: { multiplier: phpRound(weaponAttachedMultiplier, 2), gp_delta: phpIntRound(totalContentGp * (weaponAttachedMultiplier - 1)) },
            slot_penalty: { multiplier: phpRound(slotPenalty, 2), gp_delta: phpIntRound(totalContentGp * (slotPenalty - 1)) },
            economy_details: econ.steps, final_multiplier: phpRound(econ.multiplier, 3), final_gp: finalGp, pl: plOf(finalGp),
        };
        acc.totalGp += finalGp;
    }

    // NATURAL WEAPON BASE DAMAGE + REACH
    if (t === 'natural_weapon') {
        let baseWeaponGp = damageCostGpDetailed(itemArr as ItemFieldRow, false, 1, true, (r, nm) => env.ctx.layerValue(r, nm), env.contract).gp;
        baseWeaponGp = phpIntRound(baseWeaponGp * 1.15 * slotPenalty);
        const reach = phpInt(readItemNumericSlot(lv(env, itemArr, 'weapon_reach') ?? 5, 5));
        if (reach > 5) {
            const extraFt = reach - 5;
            const reachGp = phpIntRound((extraFt / 5) * 3500);
            baseWeaponGp += reachGp;
            bucket(acc, 'special').reach_bonus = { reach, extra_ft: extraFt, gp: reachGp, pl: plOf(reachGp) };
        }
        const diceN = phpInt(readItemNumericSlot(lv(env, itemArr, 'dice') ?? 0, 0));
        const dieN = phpInt(readItemNumericSlot(lv(env, itemArr, 'die') ?? 0, 0));
        bucket(acc, 'special').natural_weapon_base = { dice: diceN + 'd' + dieN, reach, gp: baseWeaponGp, pl: plOf(baseWeaponGp) };
        acc.totalGp += baseWeaponGp;
    }
}
